<template>
  <div class="student-exam-detail">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <el-button icon="el-icon-arrow-left" @click="goBack">返回</el-button>
        <h2>{{ examInfo.examName || '考试详情' }}</h2>
      </div>
      <div class="header-actions">
        <el-button type="primary" icon="el-icon-refresh" @click="refreshData">刷新</el-button>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container" v-loading="loading" element-loading-text="正在加载考试详情...">
    </div>

    <!-- 考试详情内容 -->
    <div v-else-if="examInfo.id" class="exam-content">
      <!-- 得分统计 -->
      <div class="score-summary">
        <h3><i class="el-icon-medal"></i> 得分情况</h3>
        <div class="score-cards">
          <div class="score-card total">
            <div class="score-icon">
              <i class="el-icon-trophy"></i>
            </div>
            <div class="score-content">
              <div class="score-number">{{ examInfo.finalScore || 0 }}</div>
              <div class="score-label">总分</div>
            </div>
          </div>
          <div class="score-card percentage">
            <div class="score-icon">
              <i class="el-icon-pie-chart"></i>
            </div>
            <div class="score-content">
              <div class="score-number">{{ scorePercentage }}%</div>
              <div class="score-label">得分率</div>
            </div>
          </div>
          <div class="score-card level">
            <div class="score-icon">
              <i class="el-icon-star-on"></i>
            </div>
            <div class="score-content">
              <div class="score-number">{{ scoreLevel }}</div>
              <div class="score-label">等级</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 试卷图片展示 -->
      <div class="paper-images" v-if="paperImages.length > 0">
        <h3><i class="el-icon-picture"></i> 试卷图片</h3>
        <div class="images-grid">
          <div
            v-for="(image, index) in paperImages"
            :key="index"
            class="image-item"
            @click="previewImage(image, index)"
          >
            <el-image
              :src="image"
              :preview-src-list="paperImages"
              :initial-index="index"
              fit="cover"
              class="paper-image"
            >
              <div slot="error" class="image-slot">
                <i class="el-icon-picture-outline"></i>
              </div>
            </el-image>
            <div class="image-label">第{{ index + 1 }}页</div>
          </div>
        </div>
      </div>

      <!-- 题目详情 -->
      <div class="questions-detail" v-if="questions.length > 0">
        <h3><i class="el-icon-document"></i> 题目详情</h3>
        <div class="questions-list">
          <div
            v-for="(question, index) in questions"
            :key="question.questionId"
            class="question-item"
          >
            <div class="question-header">
              <div class="question-number">第{{ index + 1 }}题</div>
              <div class="question-score">
                <el-tag :type="getScoreTagType(question.finalScore, question.questionScore)" size="small">
                  {{ question.finalScore || 0 }}/{{ question.questionScore }}
                </el-tag>
              </div>
            </div>

            <div class="question-content">
              <div class="question-title">{{ question.title || '题目内容' }}</div>

              <!-- 选择题选项 -->
              <div v-if="question.questionType <= 3 && question.options" class="question-options">
                <div
                  v-for="(option, optIndex) in question.options"
                  :key="optIndex"
                  class="option-item"
                  :class="{
                    'correct': question.correctAnswer === option.id,
                    'selected': question.studentAnswer === option.id
                  }"
                >
                  <span class="option-label">{{ String.fromCharCode(65 + optIndex) }}.</span>
                  <span class="option-content">{{ option.content }}</span>
                  <span v-if="option.image" class="option-image">
                    <el-image :src="option.image" style="max-width: 100px; max-height: 60px;"></el-image>
                  </span>
                </div>
              </div>

              <!-- 主观题答案 -->
              <div v-if="question.questionType === 4" class="subjective-answer">
                <div class="answer-label">学生答案：</div>
                <div class="answer-content">{{ question.studentAnswer || '未作答' }}</div>
              </div>

              <!-- 标准答案 -->
              <div v-if="question.correctAnswer" class="correct-answer">
                <div class="answer-label">标准答案：</div>
                <div class="answer-content">{{ question.correctAnswer }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="!loading && questions.length === 0 && paperImages.length === 0" class="empty-state">
        <i class="el-icon-document"></i>
        <p>暂无考试详情</p>
      </div>
    </div>
  </div>
</template>

<script>
import { examApi } from '@/api/exam'

export default {
  name: 'StudentExamDetail',
  data() {
    return {
      loading: false,
      examInfo: {},
      questions: [],
      paperImages: [],
      examId: null,
      courseId: null
    }
  },
  computed: {
    // 计算得分率
    scorePercentage() {
      if (!this.examInfo.totalScore || this.examInfo.totalScore === 0) return 0
      const finalScore = this.examInfo.finalScore || 0
      return Math.round((finalScore / this.examInfo.totalScore) * 100)
    },

    // 计算等级
    scoreLevel() {
      const percentage = this.scorePercentage
      if (percentage >= 90) return '优秀'
      if (percentage >= 80) return '良好'
      if (percentage >= 70) return '中等'
      if (percentage >= 60) return '及格'
      return '不及格'
    }
  },
  created() {
    this.loadExamDetail()
  },
  methods: {
    // 加载考试详情
    async loadExamDetail() {
      this.loading = true
      try {
        // 获取路由参数
        this.examId = this.$route.query.examId
        this.courseId = this.$route.query.courseId

        if (!this.examId) {
          this.$message.error('缺少考试ID参数')
          return
        }

        // 获取考试基本信息
        const examResponse = await examApi.getExamById(this.courseId, this.examId)
        if (examResponse && examResponse.code === 1) {
          this.examInfo = examResponse.data
        }

        // 获取学生答题详情
        try {
          const detailResponse = await examApi.getStudentExamDetail(this.courseId, this.examId)
          if (detailResponse && detailResponse.code === 1) {
            const detailData = detailResponse.data
            this.questions = detailData.questions || []
            this.paperImages = detailData.paperImages || []

            // 更新考试信息中的分数
            if (detailData.score !== undefined) {
              this.examInfo.finalScore = detailData.score
            }
          } else {
            // 如果后端接口未实现，使用模拟数据
            this.loadMockData()
          }
        } catch (error) {
          console.warn('获取学生答题详情失败，使用模拟数据:', error)
          this.loadMockData()
        }
      } catch (error) {
        console.error('加载考试详情失败:', error)
        this.$message.error('加载考试详情失败')
      } finally {
        this.loading = false
      }
    },

    // 加载模拟数据
    loadMockData() {
      this.examInfo = {
        id: this.examId,
        examName: '模拟考试',
        totalScore: 100,
        finalScore: 85
      }

      this.questions = [
        {
          questionId: 1,
          questionType: 1,
          title: '这是一道单选题',
          questionScore: 10,
          finalScore: 10,
          options: [
            { id: 'A', content: '选项A' },
            { id: 'B', content: '选项B' },
            { id: 'C', content: '选项C' },
            { id: 'D', content: '选项D' }
          ],
          studentAnswer: 'A',
          correctAnswer: 'A'
        },
        {
          questionId: 2,
          questionType: 4,
          title: '这是一道主观题',
          questionScore: 20,
          finalScore: 18,
          studentAnswer: '这是学生的答案',
          correctAnswer: '这是标准答案'
        }
      ]

      this.paperImages = [
        'https://via.placeholder.com/800x1000/cccccc/666666?text=试卷第1页',
        'https://via.placeholder.com/800x1000/cccccc/666666?text=试卷第2页'
      ]
    },

    // 刷新数据
    async refreshData() {
      await this.loadExamDetail()
    },

    // 返回上一页
    goBack() {
      this.$router.go(-1)
    },

    // 预览图片
    previewImage(image, index) {
      // Element UI的图片预览会自动处理
    },

    // 获取分数标签类型
    getScoreTagType(score, totalScore) {
      if (!score || !totalScore) return 'info'
      const percentage = (score / totalScore) * 100
      if (percentage >= 80) return 'success'
      if (percentage >= 60) return 'warning'
      return 'danger'
    }
  }
}
</script>

<style scoped>
.student-exam-detail {
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-left h2 {
  margin: 0;
  color: #303133;
  font-size: 20px;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.loading-container {
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.exam-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 得分统计 */
.score-summary {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.score-summary h3 {
  margin: 0 0 20px 0;
  color: #303133;
  font-size: 16px;
}

.score-cards {
  display: flex;
  gap: 20px;
}

.score-card {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  color: white;
}

.score-card.total {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.score-card.percentage {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.score-card.level {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.score-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 24px;
}

.score-content {
  flex: 1;
}

.score-number {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 5px;
}

.score-label {
  font-size: 14px;
  opacity: 0.9;
}

/* 试卷图片 */
.paper-images {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.paper-images h3 {
  margin: 0 0 20px 0;
  color: #303133;
  font-size: 16px;
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.image-item {
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s;
}

.image-item:hover {
  transform: translateY(-2px);
}

.paper-image {
  width: 100%;
  height: 150px;
}

.image-label {
  padding: 8px;
  text-align: center;
  background: #f8f9fa;
  font-size: 12px;
  color: #666;
}

.image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 30px;
}

/* 题目详情 */
.questions-detail {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.questions-detail h3 {
  margin: 0 0 20px 0;
  color: #303133;
  font-size: 16px;
}

.questions-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.question-item {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 20px;
  background: #fafafa;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.question-number {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.question-content {
  margin-top: 15px;
}

.question-title {
  font-size: 14px;
  color: #606266;
  margin-bottom: 15px;
  line-height: 1.5;
}

.question-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid #e4e7ed;
}

.option-item.correct {
  background: #f0f9ff;
  border-color: #67c23a;
}

.option-item.selected {
  background: #e1f3d8;
  border-color: #67c23a;
}

.option-label {
  font-weight: bold;
  margin-right: 8px;
  min-width: 20px;
}

.option-content {
  flex: 1;
}

.option-image {
  margin-left: 10px;
}

.subjective-answer,
.correct-answer {
  margin-top: 15px;
  padding: 15px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}

.answer-label {
  font-weight: bold;
  color: #303133;
  margin-bottom: 8px;
}

.answer-content {
  color: #606266;
  line-height: 1.5;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-state p {
  margin: 0;
  font-size: 16px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .score-cards {
    flex-direction: column;
  }

  .images-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }

  .question-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>
